import { Box, Chip, Skeleton, useTheme } from "@mui/material";

import { dayjs } from "@thalamos/common";
import type {
  IncidentWorkItemWithContext,
  UserIdUnbranded,
} from "@thalamos/vision-common";
import {
  calculateActions,
  defaultActions,
  ParticipantRoleSchema,
  tk,
} from "@thalamos/vision-common";

import type { apiHooks } from "../../api/index.js";
import {
  EmptyIncidentWorkItemCard,
  IncidentWorkItemCard,
  TypographyI18N,
} from "../../components/index.js";
import { useUser } from "../../Context/UserContext.js";
import { useIncidentActions } from "./useIncidentActions.js";

export const IncidentListModeOptions = {
  myIncidents: "myIncidents",
  transferredIncidents: "transferredIncidents",
} as const;

export type IncidentListProps = {
  incidentWorkItems: IncidentWorkItemWithContext[];
  isLoading: boolean;
  mode: "myIncidents" | "transferredIncidents";
  userId: UserIdUnbranded | undefined;
  refetchWorkItems: ReturnType<
    typeof apiHooks.incidentWorkItem.getMyActiveIncidentWorkItems
  >[1];
};

export const IncidentList = ({
  incidentWorkItems,
  isLoading,
  mode,
  userId,
  refetchWorkItems,
}: IncidentListProps) => {
  const { user } = useUser();
  const theme = useTheme();
  const {
    isPendingTransfer,
    canCancelTransfer,
    handleViewIncident,
    handleSecondaryAction,
  } = useIncidentActions(refetchWorkItems);

  const modeConfig = {
    myIncidents: {
      showAssignedOfficerOnMobile: false,
      titleKey: tk("incidentWorkItem.myIncidents"),
      emptyKey: tk("incidentWorkItem.yourIncidentsHere"),
      backgroundColor: theme.palette.primary.main,
    },
    transferredIncidents: {
      showAssignedOfficerOnMobile: true,
      titleKey: tk("incidentWorkItem.transferredIncidents"),
      emptyKey: tk("incidentWorkItem.transferredIncidentsHere"),
      backgroundColor: theme.palette.error.main,
    },
  };

  const { showAssignedOfficerOnMobile, titleKey, emptyKey, backgroundColor } =
    modeConfig[mode];

  const getMatchingParticipant = (incident: IncidentWorkItemWithContext) =>
    incident.participants.find((p) => p.participantId === userId);

  return (
    <Box
      sx={{
        display: "flex",
        gap: "0.5rem",
        flexDirection: "column",
        marginTop: "1rem",
      }}
    >
      <Box sx={{ display: "flex" }}>
        <TypographyI18N translationKey={titleKey} fontWeight={"bold"} />
        <Chip
          data-testid="incident-list-count"
          label={isLoading ? ".." : incidentWorkItems.length}
          size="small"
          sx={{
            backgroundColor,
            color: theme.palette.warning.contrastText,
            marginLeft: "0.5rem",
          }}
        />
      </Box>
      {isLoading ? (
        <Skeleton
          data-testid="incident-list-skeleton"
          sx={{
            padding: 0,
            width: "100%",
            [theme.breakpoints.down("md")]: {
              minHeight: "10rem",
            },
            [theme.breakpoints.up("md")]: {
              minHeight: "3rem",
            },
            transform: "unset",
          }}
        />
      ) : incidentWorkItems.length > 0 ? (
        <>
          {incidentWorkItems.map((incident) => {
            const matchingParticipant = getMatchingParticipant(incident);
            const isPending = isPendingTransfer(matchingParticipant);
            const canCancel = canCancelTransfer(matchingParticipant);

            const showSecondary = canCancel || isPending;

            const actions = user
              ? calculateActions({
                  incidentWorkItem: incident,
                  user,
                  asOfDateTime: dayjs(),
                })
              : defaultActions;

            return (
              <IncidentWorkItemCard
                key={incident.incidentWorkItem.id}
                incidentWorkItem={incident}
                primaryButton={{
                  label: isPending
                    ? tk("incidentWorkItem.acceptTransfer")
                    : tk("incidentWorkItem.view"),
                  disabled:
                    matchingParticipant?.role !==
                      ParticipantRoleSchema.enum.owner &&
                    !actions.pdf.canView &&
                    mode === IncidentListModeOptions.transferredIncidents,
                  onClick: () => {
                    void handleViewIncident(incident, matchingParticipant);
                  },
                }}
                {...(showSecondary
                  ? {
                      secondaryButton: {
                        label: isPending
                          ? tk("incidentWorkItem.reject")
                          : tk("incidentWorkItem.cancel"),
                        onClick: async () => {
                          await handleSecondaryAction(
                            incident,
                            matchingParticipant,
                          );
                        },
                      },
                    }
                  : {})}
                showAssignedOfficerOnMobile={showAssignedOfficerOnMobile}
                isPendingTransfer={isPending}
              />
            );
          })}
        </>
      ) : (
        <EmptyIncidentWorkItemCard title={emptyKey} />
      )}
    </Box>
  );
};
